import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useDropzone } from 'react-dropzone';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import constants from '../../../utilities/constants';
import RequestManager from '../../../utilities/requestManager';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';

const BUCKET = 'skyway-dev-373d5.appspot.com';

const HEIGHTS = { sm: 96, md: 130 };

// storageFolder    — Firebase Storage folder, e.g. "customer_data"
// storageFileName  — base filename without extension, e.g. "abc123-license"
// firestorePath    — Firestore document path, e.g. "customers/abc123"
// fieldId          — field name on that document
// currentValue     — current storage path (from car/customer data)
// onUpdate(id, v)  — called after write so parent can update local state
// size             — 'sm' | 'md' | 'lg'
export default function DocumentSlot({
  label,
  hint,
  Icon,
  fieldId,
  storageFolder,
  storageFileName,
  firestorePath,
  currentValue,
  onUpdate,
  size = 'md',
  disabled = false,
}) {
  const [loading, setLoading] = React.useState(false);
  const [filepath, setFilepath] = React.useState(currentValue || null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const replaceRef = React.useRef(null);

  React.useEffect(() => {
    setFilepath(currentValue || null);
  }, [currentValue]);

  React.useEffect(() => {
    if (!filepath) { setPreviewUrl(null); return; }
    firebase.app().storage(BUCKET).ref(filepath).getDownloadURL()
      .then(url => setPreviewUrl(url))
      .catch(() => setPreviewUrl(null));
  }, [filepath]);

  const doUpload = async (file) => {
    setLoading(true);
    try {
      // iOS often hands us a HEIC, or a camera capture with no extension at all.
      const rawExt = (file.name && file.name.includes('.') ? file.name.split('.').pop() : '').toLowerCase();
      const ext = rawExt || (file.type === 'application/pdf' ? 'pdf' : 'jpg');

      let fullPath = `${storageFolder}/${storageFileName}.${ext}`;
      const storageRef = firebase.app().storage(BUCKET).ref(fullPath);
      StateManager.setAlertAndOpen('Upload started — you can keep working.', 'info');

      const result = await StateManager.uploadFile({ storageRef, file });
      if (result) {
        // Convert HEIC/HEIF to JPEG server-side so the browser can display it.
        // The function replaces the original, so only store the returned path.
        if (['heic', 'heif'].includes(ext)) {
          try {
            const data = await RequestManager.post({ function: 'convertHeicToJpeg', variables: { storagePath: fullPath } });
            if (data && data.success && data.jpegPath) fullPath = data.jpegPath;
          } catch { /* non-fatal — the original file is still on Storage */ }
        }
        setFilepath(fullPath);
        if (constants.isValidDocPath(firestorePath)) {
          await firebase.firestore().doc(firestorePath).set({ [fieldId]: fullPath }, { merge: true });
        }
        onUpdate?.(fieldId, fullPath);
      }
    } catch (err) {
      StateManager.setAlertAndOpen(`Upload failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: 'image/*,.pdf,.heic,.heif',
    disabled: disabled || loading || !!filepath,
    onDrop: (files) => { if (files.length) doUpload(files[0]); },
  });

  const viewFile = async () => {
    if (!filepath) return;
    const url = await firebase.app().storage(BUCKET).ref(filepath).getDownloadURL();
    window.open(url, '_blank');
  };

  const handleReplace = (e) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
    e.target.value = '';
  };

  const deleteFile = async () => {
    if (!window.confirm(`Remove this ${label.toLowerCase()}?`)) return;
    setLoading(true);
    try { await firebase.app().storage(BUCKET).ref(filepath).delete(); } catch {}
    if (constants.isValidDocPath(firestorePath)) {
      await firebase.firestore().doc(firestorePath).set({ [fieldId]: null }, { merge: true });
    }
    onUpdate?.(fieldId, null);
    setFilepath(null);
    setPreviewUrl(null);
    setLoading(false);
  };

  const isLg = size === 'lg';
  const height = isLg ? undefined : HEIGHTS[size] || 96;
  const isImage = !!previewUrl && !['.pdf', '.heic', '.heif'].some(e => (filepath || '').toLowerCase().endsWith(e));

  const dropzoneContent = (
    <>
      {Icon && <Icon sx={{ fontSize: isLg ? 28 : 22, color: 'text.disabled' }} />}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant={isLg ? 'body2' : 'caption'}
          sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}
        >
          {isDragActive ? 'Drop here' : label}
        </Typography>
        {hint && !isDragActive && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10.5, display: 'block', mt: 0.25 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </>
  );

  return (
    <Box sx={{ flex: isLg ? undefined : 1, minWidth: 0 }}>
      {/* Label above slot for sm/md */}
      {!isLg && (
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, display: 'block' }}>
          {label}
        </Typography>
      )}

      <input ref={replaceRef} type="file" accept="image/*,.pdf,.heic,.heif" style={{ display: 'none' }} onChange={handleReplace} />

      {loading ? (
        <Box sx={{
          height: height || 100,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
          border: '1px dashed', borderColor: 'divider', borderRadius: 1.5,
        }}>
          <CircularProgress size={20} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploading…</Typography>
        </Box>
      ) : filepath ? (
        <Box sx={{
          position: 'relative',
          height: height,
          minHeight: isLg ? 100 : undefined,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'success.light',
          bgcolor: (t) => !isImage ? alpha(t.palette.success.main, 0.07) : undefined,
        }}>
          {isImage ? (
            <img
              src={previewUrl}
              alt={label}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box sx={{
              height: '100%',
              minHeight: isLg ? 100 : undefined,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 0.75, p: 2,
            }}>
              <CheckCircleIcon sx={{ fontSize: 26, color: 'success.main' }} />
              <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600, textAlign: 'center' }}>
                {label} on file
              </Typography>
            </Box>
          )}

          {/* Hover overlay */}
          <Box sx={{
            position: 'absolute', inset: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
            opacity: 0, transition: 'opacity 0.15s',
            '&:hover': { opacity: 1 },
          }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={viewFile} data-sold-ok sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {!disabled && (
              <Tooltip title="Replace">
                <IconButton size="small" onClick={() => replaceRef.current?.click()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {!disabled && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={deleteFile} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          {...getRootProps()}
          sx={{
            height: height,
            minHeight: isLg ? 100 : undefined,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: isLg ? 1.5 : 0.75,
            p: isLg ? 3 : 1,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            bgcolor: isDragActive ? (t) => alpha(t.palette.primary.main, 0.05) : 'grey.50',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': disabled ? {} : {
              borderColor: 'primary.main',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
            },
          }}
        >
          <input {...getInputProps()} />
          {dropzoneContent}
        </Box>
      )}
    </Box>
  );
}
